import { useCallback, useEffect, useState } from 'react';
import axios from '../../../axiosConfig';

/**
 * Receivables list + aging summary + mutations. Default status filter
 * is 'open' which pulls OPEN + PARTIALLY_PAID (backend treats missing
 * `status` as that combined view). Pass an explicit status to filter
 * to a single bucket: 'PAID', 'WRITTEN_OFF', etc.
 */
export default function useReceivables({ status = 'open' } = {}) {
  const [items, setItems] = useState([]);
  const [aging, setAging] = useState({
    totalOwed: 0,
    openCount: 0,
    buckets: {
      current:      { count: 0, amount: 0 },
      bucket0_30:   { count: 0, amount: 0 },
      bucket31_60:  { count: 0, amount: 0 },
      bucket61_90:  { count: 0, amount: 0 },
      bucket90plus: { count: 0, amount: 0 },
    },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchList = useCallback(async (statusFilter) => {
    setLoading(true);
    setError(null);
    try {
      // Aging is computed from open/partial regardless of the list filter
      // — it's a fixed dashboard signal, not a filtered view.
      const params = {};
      if (statusFilter && statusFilter !== 'open') {
        params.status = statusFilter;
      }
      const [listRes, agingRes] = await Promise.all([
        axios.get('/finance/receivables', { params }),
        axios.get('/finance/receivables/aging'),
      ]);
      setItems(listRes.data?.items || []);
      if (agingRes.data) {
        setAging({
          totalOwed: Number(agingRes.data.totalOwed || 0),
          openCount: Number(agingRes.data.openCount || 0),
          buckets: agingRes.data.buckets || aging.buckets,
        });
      }
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load receivables';
      setError(msg);
      setItems([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchList(status);
  }, [status, fetchList]);

  const createIou = useCallback(async (payload) => {
    const res = await axios.post('/finance/receivables', payload);
    return res.data?.item;
  }, []);

  const recordPayment = useCallback(async (receivableId, payload) => {
    const res = await axios.post(`/finance/receivables/${receivableId}/payments`, payload);
    return res.data;
  }, []);

  const writeOff = useCallback(async (receivableId, reason) => {
    const res = await axios.put(`/finance/receivables/${receivableId}/write-off`, { reason });
    return res.data?.receivable;
  }, []);

  const remind = useCallback(async (receivableId) => {
    const res = await axios.post(`/finance/receivables/${receivableId}/remind`);
    return res.data;
  }, []);

  return {
    items,
    aging,
    loading,
    error,
    refetch: () => fetchList(status),
    createIou,
    recordPayment,
    writeOff,
    remind,
  };
}
